import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import supabase from '../lib/supabaseClient';
import { VISIBLE_TENANTS_OR } from '../lib/tenantVisibility';
import { fetchWithCsrf } from '../lib/fetchWithCsrf';
import Button from './ui/button';
import { RefreshCw, AlertTriangle, CheckCircle, Eye, Play } from 'lucide-react';

const InstructionRebuildTool = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('all');
  const [selectedCampaigns, setSelectedCampaigns] = useState('all');
  const [previewResults, setPreviewResults] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch all tenants
  const fetchTenants = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('tenants')
        .select('id, name')
        .or(VISIBLE_TENANTS_OR) // hide inactive/suspended from global_admin dropdown
        .order('name');
      
      if (error) throw error;
      
      if (data) {
        setTenants(data);
        console.log('✅ Tenants loaded:', data.length);
      }
    } catch (err) {
      console.error('⚠️ Could not fetch tenants:', err.message);
      setError('Failed to load tenants');
    }
  }, []);

  // Fetch campaigns based on selected tenant
  const fetchCampaigns = useCallback(async () => {
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      let url = `${API_BASE}/campaigns`;

      // Filter by tenant if not "all"
      if (selectedTenant !== 'all') {
        url += `?tenant_id=${selectedTenant}`;
      }

      const response = await fetchWithCsrf(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch campaigns: ${response.status}`);
      }

      const data = await response.json();

      // Filter out archived campaigns
      const activeCampaigns = data.filter(campaign => !campaign.archived);

      setCampaigns(activeCampaigns);
      console.log('✅ Campaigns loaded:', activeCampaigns.length);
    } catch (err) {
      console.error('⚠️ Could not fetch campaigns:', err.message);
      setError('Failed to load campaigns');
    }
  }, [selectedTenant]);

  // Load tenants on mount
  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  // Reload campaigns when tenant filter changes
  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handlePreviewChanges = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    setPreviewResults(null);

    try {
      // Build the request payload
      const payload = {
        dryRun: previewMode,
        tenantId: selectedTenant === 'all' ? null : selectedTenant,
        campaignIds: selectedCampaigns === 'all' ? null : [selectedCampaigns]
      };

      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      // Get token directly from Supabase session to avoid localStorage collision with CS extension
      const { data: { session } } = await supabase.auth.getSession();
      const authToken = session?.access_token;

      console.log('🔍 Making rebuild request with payload:', payload);
      const response = await fetchWithCsrf(`${API_BASE}/campaigns/rebuild-instructions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(payload)
      });

      console.log('📡 Response status:', response.status);
      if (!response.ok) {
        const errorBody = await response.text();
        console.error('❌ Error response body:', errorBody);
        throw new Error(`Failed to rebuild instructions: ${response.status}`);
      }

      const data = await response.json();

      if (previewMode) {
        setPreviewResults(data);
        setSuccess(`Preview complete: ${data.campaignsToUpdate || 0} campaigns would be updated`);
      } else {
        setSuccess(`Successfully rebuilt instructions for ${data.updatedCount || 0} campaigns`);
      }
    } catch (err) {
      console.error('Error rebuilding instructions:', err);
      setError(`Failed to rebuild instructions: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyChanges = async () => {
    if (!window.confirm('Are you sure you want to apply these changes? All selected campaign instructions will be rebuilt.')) {
      return;
    }

    setPreviewMode(false);
    await handlePreviewChanges();
    setPreviewMode(true); // Reset back to preview mode
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <RefreshCw className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              AI Instruction Rebuild Tool
            </h2>
            <p className="text-gray-600">
              Safely rebuild all campaign instructions with updated system logic while preserving configurations.
            </p>
          </div>
        </div>
      </div>

      {/* Warning */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start space-x-3">
        <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-yellow-900 mb-1">Important</p>
          <p className="text-sm text-yellow-800">
            Always run in DRY RUN mode first to preview changes before applying them. 
            All instructions are automatically backed up before rebuild.
          </p>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center space-x-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-red-800 font-medium">{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <span className="text-green-800 font-medium">{success}</span>
        </div>
      )}

      {/* Configuration Form */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
        {/* Rebuild Mode */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Rebuild Mode
          </label>
          <select
            value={previewMode ? 'preview' : 'apply'}
            onChange={(e) => setPreviewMode(e.target.value === 'preview')}
            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={loading}
          >
            <option value="preview">🔍 Dry Run (Preview Only)</option>
            <option value="apply">⚡ Apply Changes (Live)</option>
          </select>
          <p className="text-sm text-gray-500 mt-2">
            {previewMode 
              ? 'Shows what would change without modifying database'
              : 'WARNING: This will modify campaign instructions in the database'}
          </p>
        </div>

        {/* Tenant Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filter by Tenant
          </label>
          <select
            value={selectedTenant}
            onChange={(e) => {
              setSelectedTenant(e.target.value);
              setSelectedCampaigns('all'); // Reset campaign selection when tenant changes
            }}
            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={loading}
          >
            <option value="all">All Tenants</option>
            {tenants.map(tenant => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name || `Tenant ${tenant.id}`}
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-2">
            Select a specific tenant or rebuild for all tenants
          </p>
        </div>

        {/* Campaign Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filter by Campaign
          </label>
          <select
            value={selectedCampaigns}
            onChange={(e) => setSelectedCampaigns(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={loading || campaigns.length === 0}
          >
            <option value="all">
              All Campaigns {campaigns.length > 0 && `(${campaigns.length})`}
            </option>
            {campaigns.map(campaign => (
              <option key={campaign.id} value={campaign.id}>
                {campaign.name}
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-2">
            {campaigns.length === 0 
              ? 'No campaigns available for selected tenant'
              : 'Select a specific campaign or rebuild all active campaigns'}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3 pt-4">
          {previewMode ? (
            <Button
              onClick={handlePreviewChanges}
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Eye className="w-4 h-4 mr-2" />
              {loading ? 'Generating Preview...' : 'Preview Changes'}
            </Button>
          ) : (
            <Button
              onClick={handleApplyChanges}
              disabled={loading}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              <Play className="w-4 h-4 mr-2" />
              {loading ? 'Applying Changes...' : 'Apply Changes'}
            </Button>
          )}
        </div>
      </div>

      {/* Preview Results */}
      {previewResults && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview Results</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-600">Campaigns to Update:</span>
              <span className="font-semibold text-gray-900">{previewResults.campaignsToUpdate || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-600">Total Instructions:</span>
              <span className="font-semibold text-gray-900">{previewResults.totalInstructions || 0}</span>
            </div>
            {previewResults.affectedTenants && (
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-600">Affected Tenants:</span>
                <span className="font-semibold text-gray-900">{previewResults.affectedTenants.length}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* How It Works */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            Safety Features
          </h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start">
              <span className="text-green-600 mr-2">✓</span>
              Automatic backup before any changes
            </li>
            <li className="flex items-start">
              <span className="text-green-600 mr-2">✓</span>
              Dry-run preview mode
            </li>
            <li className="flex items-start">
              <span className="text-green-600 mr-2">✓</span>
              Validation of required sections
            </li>
            <li className="flex items-start">
              <span className="text-green-600 mr-2">✓</span>
              One-click rollback capability
            </li>
          </ul>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
              <RefreshCw className="w-5 h-5 text-blue-600" />
            </div>
            What Gets Rebuilt
          </h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              Initial instruction
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              Engagement instruction
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              Follow-up instructions (1, 2, 3)
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              Preserves tenant configurations
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default InstructionRebuildTool;
